/**
 * User API Hooks
 *
 * React Query hooks for user CRUD operations
 * Based on user-controller API endpoints
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userQueryKeys } from './user.keys';
import { userService } from './user.service';
import type {
  UserCreateRequest,
  UserUpdateRequest,
  UserListParams,
} from '../types/user.types';

export function useUsersQuery(params: UserListParams = {}) {
  const { page = 0, size = 20, sort } = params;

  return useQuery({
    queryKey: userQueryKeys.list(params),
    queryFn: () =>
      userService.getUsers({
        page,
        size,
        sort,
      }),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useUserQuery(id: string | null) {
  return useQuery({
    queryKey: userQueryKeys.detail(id || ''),
    queryFn: () => {
      if (!id) throw new Error('User ID is required');
      return userService.getUserById(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useOtherUsersQuery() {
  return useQuery({
    queryKey: userQueryKeys.otherUsers(),
    queryFn: () => userService.getOtherUsers(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreateRequest) => userService.createUser(data),
    onSuccess: () => {
      // Invalidate users list
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.lists(),
      });
      // Also invalidate other users list
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.otherUsers(),
      });
    },
    onError: (error) => {
      console.error('[User API] Create failed:', error);
    },
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdateRequest }) =>
      userService.updateUser(id, data),
    onSuccess: (data) => {
      // Invalidate and refetch the specific user
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail(data.userId),
      });
      // Also invalidate all user lists
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.otherUsers(),
      });
    },
    onError: (error) => {
      console.error('[User API] Update failed:', error);
    },
  });
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: (_, id) => {
      // Invalidate the deleted user
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail(id),
      });
      // Invalidate all user lists
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.otherUsers(),
      });
    },
    onError: (error) => {
      console.error('[User API] Delete failed:', error);
    },
  });
}
